import React from "react";
import PropTypes from "prop-types";
import { Route, Redirect } from "react-router-dom";
import queryString from "query-string";
import {
  ADMIN,
  CANDIDATE,
  ORGANIZATION,
  RECRUITER,
} from "../../common/constant/LoginEnum";

// Helper to decode JWT and check expiry
const isSessionExpired = (authData) => {
  if (!authData) return true;
  try {
    const token =
      authData.token ||
      authData.accessToken ||
      authData.jwtToken ||
      authData.access_token;
    if (!token) return false;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return false; // If token can't be parsed, don't block
  }
};

// Helper to clear all session data
const clearSession = () => {
  localStorage.removeItem("authUser");
  localStorage.removeItem("setpassword");
  localStorage.removeItem("prevRoute");
  localStorage.removeItem("jobId");
};

const Authmiddleware = ({
  component: Component,
  layout: Layout,
  isAuthProtected,
  ...rest
}) => {
  let previousRoute = "";

  return (
    <Route
      {...rest}
      render={(props) => {
        if (localStorage.getItem("languageChanged")) {
          localStorage.removeItem("languageChanged");
          window.location.reload();
          return;
        }

        const data = JSON.parse(localStorage.getItem("authUser"));
        if (props.location.pathname === "/") {
          return (
            <Redirect
              to={{
                pathname: "/login/organization",
                state: { from: props.location },
              }}
            />
          );
        }

        const isFeedbackOrPublicRoute =
          props.location.pathname === "/feedback/interview" ||
          props.location.pathname === "/feedback/job/interest" ||
          props.location.pathname === "/feedback/candidate/assessment" ||
          props.location.pathname === "/shortlist/feedback" ||
          props.location.pathname === "/offer/feedback" ||
          props.location.pathname === "/feedback/interview/:candidateId" ||
          props.location.pathname === "/hire/feedback" ||
          props.location.pathname.includes("meeting") ||
          props.location.pathname === "/submit/document" ||
          props.location.pathname === "/webhook/email/unsubscribe" ||
          props.location.pathname === "/feedback/error" ||
          props.location.pathname.includes("/feedback/interview") ||
          props.location.pathname === "/impersonate";

        if (isFeedbackOrPublicRoute) {
          return (
            <Layout>
              <Component {...props} />
            </Layout>
          );
        }

        // Session expiry check — runs for ALL protected routes
        if (data && isSessionExpired(data)) {
          clearSession();
          return (
            <Redirect
              to={{
                pathname: "/login/organization",
                state: { from: props.location, sessionExpired: true },
              }}
            />
          );
        }

        if (props.location.pathname === "/job/:jobId/process") {
          let pathVariables = queryString.parse(props.location.search);
          if (pathVariables.job) {
            let jobId = Buffer.from(pathVariables.job, "base64").toString();
            return (
              <Redirect
                to={{ pathname: "/viewJob", state: { job_id: jobId } }}
              />
            );
          } else if (pathVariables.jobId) {
            if (localStorage.getItem("authUser") !== null) {
              return (
                <Redirect
                  to={{
                    pathname: "/candidate/myjobs/viewjob",
                    state: { job_id: pathVariables.jobId },
                  }}
                />
              );
            } else {
              return (
                <Redirect
                  to={{
                    pathname: "/viewJob",
                    state: { job_id: pathVariables.jobId },
                  }}
                />
              );
            }
          }
        }

        if (isAuthProtected && localStorage.getItem("authUser") !== null) {
          if (props.location.pathname === "/logout") {
            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          }

          if (data.userType === CANDIDATE) {
            if (
              props.location.pathname === "/login" ||
              props.location.pathname === "/register" ||
              props.location.pathname === "/forgot-password" ||
              props.location.pathname === "/verifyAccount" ||
              props.location.pathname === "/verifyResetPassword" ||
              props.location.pathname === "/index"
            ) {
              return (
                <Redirect
                  to={{
                    pathname: "/candidatedashboard",
                    state: { from: props.location },
                  }}
                />
              );
            }
            if (
              localStorage.getItem("setpassword") === null &&
              props.location.pathname !== "/candidatedetail" &&
              props.location.pathname !== "/logout" &&
              !data.profileUpdated
            ) {
              return (
                <Redirect
                  to={{
                    pathname: "/candidatedetail",
                    state: { from: props.location },
                  }}
                />
              );
            }

            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          } else if (
            data.userType === ORGANIZATION ||
            data.userType === RECRUITER ||
            data.userType === ADMIN
          ) {
            // console.log( props.location.pathname)

            if (
              data.passwordSet === true &&
              data.profileUpdated === false &&
              props.location.pathname === "/detail" &&
              data.companyAdmin === true
            ) {
              return (
                <Layout>
                  <Component {...props} />
                </Layout>
              );
            } else if (
              data.passwordSet === true &&
              data.profileUpdated === false &&
              data.companyAdmin === true
            ) {
              return <Redirect to={{ pathname: "/detail" }} />;
            }

            if (
              props.location.pathname === "/login/organization" ||
              props.location.pathname === "/login" ||
              props.location.pathname === "/register" ||
              props.location.pathname === "/forgot-password" ||
              props.location.pathname === "/verifyAccount" ||
              props.location.pathname === "/verifyResetPassword" ||
              props.location.pathname === "/index"
            ) {
              return (
                <Redirect
                  to={{
                    pathname:
                      data.roleName == "SNA_Admin" && data.showAdminDashboard
                        ? "/admin/dashboard"
                        : data.userType === RECRUITER
                          ? "/recruiter/dashboard"
                          : "/dashboard",
                    state: { from: props.location },
                  }}
                />
              );
            }
            if (
              props.location.pathname === "/setpassword" &&
              localStorage.getItem("setpassword") === null
            ) {
              return (
                <Redirect
                  to={{
                    pathname:
                      data.roleName == "SNA_Admin" && data.showAdminDashboard
                        ? "/admin/dashboard"
                        : data.userType === RECRUITER
                          ? "/recruiter/dashboard"
                          : "/dashboard",
                    state: { from: props.location },
                  }}
                />
              );
            }

            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          } else if (data.userType === "Recruiter") {
            let redirectUrl = "/recruiter/dashboard";
            if (!data.showRecruiterCompany) {
              redirectUrl = "/recruiter/candidate/viewall";
            }
            if (
              props.location.pathname === "/login" ||
              props.location.pathname === "/register" ||
              props.location.pathname === "/forgot-password" ||
              props.location.pathname === "/verifyAccount" ||
              props.location.pathname === "/verifyResetPassword" ||
              props.location.pathname === "/index"
            ) {
              return (
                <Redirect
                  to={{
                    pathname: redirectUrl,
                    state: { from: props.location },
                  }}
                />
              );
            }
            if (
              props.location.pathname === "/setpassword" &&
              localStorage.getItem("setpassword") === null
            ) {
              return (
                <Redirect
                  to={{
                    pathname: redirectUrl,
                    state: { from: props.location },
                  }}
                />
              );
            }
            if (
              props.location.pathname !== "/setpassword" &&
              localStorage.getItem("setpassword") !== null
            ) {
              return (
                <Redirect
                  to={{
                    pathname: "/setpassword",
                    state: { from: props.location },
                  }}
                />
              );
            }

            if (
              localStorage.getItem("setpassword") === null &&
              props.location.pathname !== "/recruiter/detail" &&
              props.location.pathname !== "/logout" &&
              !data.profileUpdated
            ) {
              return (
                <Redirect
                  to={{
                    pathname: "/recruiter/detail",
                    state: { from: props.location },
                  }}
                />
              );
            }

            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          }
        }

        if (!isAuthProtected && localStorage.getItem("authUser") !== null) {
          const data = JSON.parse(localStorage.getItem("authUser"));

          if (
            data.passwordSet === false &&
            props.location.pathname === "/setpassword"
          ) {
            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          }
          if (props.location.pathname === "/logout") {
            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          }

          if (
            data.passwordSet === false &&
            props.location.pathname === "/verifyAccount"
          ) {
            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          } else if (
            data.passwordSet === true &&
            props.location.pathname === "/detail" &&
            data.companyAdmin === true
          ) {
            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          } else if (data.passwordSet === true && data.companyAdmin === true) {
            return <Redirect to={{ pathname: "/detail" }} />;
          } else if (data.passwordSet === true && data.companyAdmin === false) {
            return (
              <Redirect
                to={{ pathname: "/dashboard", state: { from: props.location } }}
              />
            );
          } else {
            return <Redirect to={{ pathname: "/setpassword" }} />;
          }
        }

        if (
          (!isAuthProtected || isAuthProtected === null) &&
          localStorage.getItem("authUser") === null
        ) {
          let path = props.location.pathname + props.location.search;
          if (
            path !== "/login" &&
            path !== "/login/organization" &&
            path !== "/login" &&
            path !== "/contactus" &&
            path !== "/register" &&
            path !== "/forgot-password" &&
            path !== "/verifyAccount" &&
            path !== "/setpassword" &&
            path !== "/resetpassword" &&
            path !== "/verifyResetPassword" &&
            path !== "/verifyResetPassword/candidate" &&
            path !== "/verifyResetPassword/organization" &&
            path !== "/index" &&
            path !== "/login/admin" &&
            path !== "/register/organization" &&
            path !== "/register" &&
            path !== "/register/candidate" &&
            path !== "/login/candidate" &&
            path !== "/forgot-password/organization" &&
            path !== "/forgot-password/candidate" &&
            path !== "/forgot-password" &&
            path !== "/terms" &&
            path !== "/logout" &&
            !path.includes("/reset") &&
            path !== "/feedback/interview" &&
            path !== "/feedback/job/interest" &&
            path !== "/feedback/candidate/assessment" &&
            path !== "/shortlist/feedback" &&
            path !== "/offer/feedback" &&
            path !== "/feedback/interview/:candidateId" &&
            path !== "/hire/feedback" &&
            !path.includes("meeting") &&
            path !== "/submit/document" &&
            path !== "/webhook/email/unsubscribe" &&
            path !== "/feedback/error" &&
            !path.includes("/feedback/interview")
          ) {
            previousRoute = props.location.pathname + props.location.search;
            localStorage.setItem(
              "prevRoute",
              JSON.stringify({ previousRoute }),
            );
          }

          if (
            props.location.pathname === "/contactus" ||
            props.location.pathname === "/login" ||
            props.location.pathname === "/register" ||
            props.location.pathname === "/forgot-password" ||
            props.location.pathname === "/verifyAccount" ||
            props.location.pathname === "/setpassword" ||
            props.location.pathname === "/resetpassword" ||
            props.location.pathname === "/verifyResetPassword" ||
            props.location.pathname === "/verifyResetPassword/candidate" ||
            props.location.pathname === "/verifyResetPassword/organization" ||
            props.location.pathname === "/index" ||
            props.location.pathname === "/login/organization" ||
            props.location.pathname === "/login" ||
            props.location.pathname === "/register/organization" ||
            props.location.pathname === "/register" ||
            props.location.pathname === "/register/candidate" ||
            props.location.pathname === "/login/candidate" ||
            props.location.pathname === "/forgot-password/organization" ||
            props.location.pathname === "/forgot-password/candidate" ||
            props.location.pathname === "/forgot-password" ||
            props.location.pathname === "/terms" ||
            props.location.pathname === "/login/admin"
          ) {
            let params = queryString.parse(props.location.search);
            let jobId1 = encodeURIComponent(params.jobId);
            if (
              jobId1 !== undefined &&
              jobId1 !== null &&
              jobId1.length > 1 &&
              jobId1 !== "undefined"
            ) {
              localStorage.setItem("jobId", jobId1);
            }

            return (
              <Layout>
                <Component {...props} />
              </Layout>
            );
          }
          if (
            props.location.pathname === "/candidate/myjobs/viewjob" ||
            props.location.pathname === "/candidatedashboard"
          ) {
            return (
              <Redirect
                to={{
                  pathname: "/login/candidate",
                  state: { from: props.location.search },
                }}
              />
            );
          }
          if (props.location.pathname === "/dashboard") {
            return (
              <Redirect
                to={{
                  pathname: "/login/organization",
                  state: { from: props.location.search },
                }}
              />
            );
          } else {
            if (props.location.pathname.includes("admin")) {
              return (
                <Redirect
                  to={{
                    pathname: "/login/admin",
                    state: { from: props.location.search },
                  }}
                />
              );
            } else {
              return (
                <Redirect
                  to={{ pathname: "/login", state: { from: props.location } }}
                />
              );
            }
          }
        }
        return (
          <Layout>
            <Component {...props} />
          </Layout>
        );
      }}
    />
  );
};

Authmiddleware.propTypes = {
  isAuthProtected: PropTypes.bool,
  component: PropTypes.any,
  location: PropTypes.object,
  layout: PropTypes.any,
};
export default Authmiddleware;
